/**
 * Teslyn Cloud's Reports page (docs/specs/portal/client-area.md §6.2; help `cloud.reports`): one project's reports at
 * a time, chosen with the Deliverables page's "Project" choice (R198, MB 4 Nov 2026). It opens on the project a link
 * names (a report email or a client notice, `?page=reports&project=<id>`), else the one last chosen in this browser,
 * else the active project with the newest report (`reportsProjectChosen`, `reportsProjectByNewest`). Staff, clients
 * and Preview as client alike.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  CLIENT_REPORT_KINDS,
  reportsProjectByNewest,
  reportsProjectChosen,
  reportsProjectKey,
} from '@teslyn/domain';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import {
  loadProjects,
  loadPublishedReports,
  loadStaffReports,
  type PortalProject,
  type Scope,
} from './api.ts';
import { ProjectPicker } from './ProjectPicker.tsx';
import { ReportsList, type ReportsListProps, type ReportsMode } from './ReportsList.tsx';

export interface ReportsScreenProps {
  readonly sb: SupabaseClient;
  readonly scope: Scope;
  readonly mode: ReportsMode;
  readonly roles?: readonly string[];
  readonly onReportOpened?: ReportsListProps['onOpened'];
  /** Whose choice this browser keeps (the signed-in person; in Preview as client, the staff member). */
  readonly personId?: string | null;
  /** The project a link opened the page on (`?page=reports&project=<id>`); one not listed is ignored. */
  readonly linkedProjectId?: string | null;
}

function readRemembered(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, projectId: string): void {
  try {
    window.localStorage.setItem(key, projectId);
  } catch {
    // storage blocked: the choice holds until the page is closed
  }
}

export function ReportsScreen({
  sb,
  scope,
  mode,
  roles,
  onReportOpened,
  personId = null,
  linkedProjectId = null,
}: ReportsScreenProps): ReactElement {
  const [projects, setProjects] = useState<PortalProject[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scopeKey = scope.kind === 'client' ? `c:${scope.clientOrgId}` : `t:${scope.tenantId}`;
  const key = reportsProjectKey(personId, scopeKey);
  const reload = useCallback(() => {
    setError(null);
    setProjects(null);
    loadProjects(sb, scope)
      .then(async (ps) => {
        // Try again keeps the project chosen while it is still listed
        const kept = (c: string | null): string | null => (c && ps.some((p) => p.id === c) ? c : null);
        const known = reportsProjectChosen(ps, linkedProjectId, readRemembered(key));
        // a link chooses its project as a person would: it is the one last chosen from now on
        if (known !== null && known === linkedProjectId) remember(key, known);
        if (known !== null || ps.length === 0) {
          setChosen((c) => kept(c) ?? known);
          setProjects(ps);
          return;
        }
        // nothing named or remembered: the active project with the newest report this person may see (a failure
        // here only loses the default; the list itself says what went wrong)
        const ids = ps.map((p) => p.id);
        const newest = await (
          mode === 'client'
            ? loadPublishedReports(sb, ids).then((r) => r.filter((x) => CLIENT_REPORT_KINDS.includes(x.kind)))
            : loadStaffReports(sb, ids)
        ).catch(() => []);
        setChosen((c) => kept(c) ?? reportsProjectByNewest(ps, newest));
        setProjects(ps);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the scope is identified by its key
  }, [sb, scopeKey, mode, key, linkedProjectId]);
  useEffect(reload, [reload]);
  const project = projects?.find((p) => p.id === chosen) ?? null;

  function choose(id: string): void {
    setChosen(id);
    remember(key, id);
  }

  return (
    <section className="page">
      <PageHeader
        title="Reports"
        subtitle={
          mode === 'client'
            ? 'Daily and weekly reports published to your organisation, one project at a time.'
            : 'Reports of the chosen project. Attach the PDF, then publish it to the project’s client.'
        }
      />
      {error && <ErrorState message={error} onRetry={reload} />}
      {!projects && !error && <Loading what="projects" />}
      {projects && projects.length === 0 && (
        <p className="body muted">
          {mode === 'client' ? 'No projects are linked to your organisation yet.' : 'No project to show.'}
        </p>
      )}
      {projects && project && (
        <>
          <ProjectPicker projects={projects} value={project.id} onChange={choose} />
          <ReportsList
            key={project.id}
            sb={sb}
            mode={mode}
            projects={[project]}
            {...(roles ? { roles } : {})}
            {...(onReportOpened ? { onOpened: onReportOpened } : {})}
          />
        </>
      )}
    </section>
  );
}
